import PropTypes from 'prop-types';
import { getIn, useFormikContext } from 'formik';
import {
  colors,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from '@mui/material';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';

const isValueEmpty = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
};

const MUISelect = ({
  label,
  options,
  error,
  helperText,
  required,
  requiredHintText,
  sx,
  ...selectProps
}) => (
  <FormControl
    error={Boolean(error)}
    fullWidth
    sx={{
      background: selectProps.disabled ? colors.grey[200] : 'inherit',
      ...sx
    }}
  >
    <InputLabel>{label}</InputLabel>
    <Select
      {...selectProps}
      input={<OutlinedInput label={label} />}
      value={selectProps.value || ''}
    >
      {options?.filter(Boolean)?.map((option) => (
        <MenuItem
          key={option.key || option.value || option.label}
          value={option.value}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
    {error ? <FormHelperText>{helperText}</FormHelperText> : null}
    {!error && required && isValueEmpty(selectProps.value) ? (
      <FormHelperText>{requiredHintText}</FormHelperText>
    ) : null}
  </FormControl>
);

MUISelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  requiredHintText: PropTypes.string,
  sx: PropTypes.object
};

MUISelect.defaultProps = {
  options: [],
  error: false,
  helperText: '',
  required: false,
  requiredHintText: 'This field is required',
  sx: {}
};

const CountryStateFields = ({
  countryField,
  stateField,
  requiredHintText
}) => {
  const {
    values,
    touched,
    errors,
    setFieldValue,
    handleBlur
  } = useFormikContext();

  const countryValue = getIn(values, countryField.name) || '';
  const stateValue = getIn(values, stateField.name) || '';
  const countryError = Boolean(getIn(touched, countryField.name) && getIn(errors, countryField.name));
  const stateError = Boolean(getIn(touched, stateField.name) && getIn(errors, stateField.name));

  const renderCountrySelect = (props) => (
    <MUISelect
      {...props}
      label={countryField.label}
      error={countryError}
      helperText={getIn(errors, countryField.name)}
      required
      requiredHintText={requiredHintText}
    />
  );

  const renderStateSelect = (props) => (
    <MUISelect
      {...props}
      label={stateField.label}
      error={stateError}
      helperText={getIn(errors, stateField.name)}
      required
      requiredHintText={requiredHintText}
    />
  );

  return (
    <>
      <Grid item xs={12} sm={6}>
        <CountryDropdown
          name={countryField.name}
          value={countryValue}
          valueType="short"
          labelType="full"
          defaultOptionLabel="Select Country"
          onBlur={handleBlur}
          onChange={(value) => {
            setFieldValue(countryField.name, value);
            if (stateValue) {
              setFieldValue(stateField.name, '');
            }
          }}
          customRender={renderCountrySelect}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <RegionDropdown
          name={stateField.name}
          country={countryValue}
          countryValueType="short"
          value={stateValue}
          valueType="short"
          labelType="full"
          defaultOptionLabel="Select State"
          disableWhenEmpty
          onBlur={handleBlur}
          onChange={(value) => {
            setFieldValue(stateField.name, value);
          }}
          customRender={renderStateSelect}
        />
      </Grid>
    </>
  );
};

CountryStateFields.propTypes = {
  countryField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  stateField: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  requiredHintText: PropTypes.string
};

CountryStateFields.defaultProps = {
  requiredHintText: 'This field is required'
};

export default CountryStateFields;
